import React from "react";
import { t } from "ttag";

import { Context } from "@reactivated";

import styles from "./index.module.scss";

/**
 * Bare domain of whichever site served this page, which is how Google
 * identifies a publication: no protocol, no `www`, and subdomains count as
 * separate sources.
 *
 * Read from the request rather than hardcoded so the badge always points at
 * the site the reader is actually on. leaningirls.org is redirect-only today
 * (every 404 there 301s to /girls/, see NON_DEFAULT_SITE_REDIRECT_TARGET), so
 * leanin.org is the only host serving articles, but that could change. On
 * non-production hosts this resolves to that host, which is inert rather than
 * wrong: it just isn't a source anyone has registered.
 */
const preferredSourceDomain = (requestUrl: string) =>
    new URL(requestUrl).hostname.replace(/^www\./, "");

// Intrinsic size of the 1x badge Google ships, declared on the element so the
// article doesn't reflow as the image loads. The 2x file is 676x213, one pixel
// short of exactly double; imperceptible, but don't assume it scales evenly if
// the asset is ever re-exported.
const BADGE_WIDTH = 338;
const BADGE_HEIGHT = 107;

/**
 * Links readers to Google's source preferences page, pre-filled with the site
 * they're reading, so they can add it as a preferred source in Search.
 *
 * This is Google's documented deeplink implementation rather than the
 * JavaScript button. The button would be the nicer interaction — it adds the
 * source in place instead of navigating away — but it loads a 251KB
 * Subscribe-with-Google bundle that reads the `__gads` advertising cookie and
 * frames news.google.com on page load. Under this site's CookieYes banner,
 * which runs GDPR opt-in for all visitors worldwide, that has to sit behind the
 * advertising category, so only readers who accept advertising cookies would
 * ever see it. A plain link reaches everyone and collects nothing.
 */
export const PreferredSourceButton = () => {
    const context = React.useContext(Context);
    const { preferred_source_badge, preferred_source_badge_2x } =
        context.core_assets;
    const srcSet = `${preferred_source_badge} 1x, ${preferred_source_badge_2x} 2x`;
    const domain = preferredSourceDomain(context.request.url);
    const deeplink = `https://www.google.com/preferences/source?q=${encodeURIComponent(domain)}`;
    return (
        <div className={styles.root}>
            <a
                className={styles.link}
                href={deeplink}
                target="_blank"
                rel="noopener noreferrer"
            >
                <img
                    className={styles.badge}
                    src={preferred_source_badge}
                    srcSet={srcSet}
                    width={BADGE_WIDTH}
                    height={BADGE_HEIGHT}
                    loading="lazy"
                    // Opens with the text printed on the badge itself, so the
                    // accessible name contains the visible label (WCAG 2.5.3).
                    alt={t`Add as a preferred source on Google (opens in a new tab)`}
                />
            </a>
        </div>
    );
};
